import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { DEFAULT_DATE_RANGE } from '../input-date/input-date.component';
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { BaseControlValueAccessor } from "../../../../../util/form/BaseControlValueAccessor";
import { LocalDate } from "../../../../../core/pojo/date-variable";
import {
  DateFormat,
  YYYY_MM_DD,
  DD_MM_YYYY,
  transformDateFormat,
} from "../../../../../util/common/date-service/date.service";
import { InputErrorClass } from "../input-component/input.component";

/**
 * A very custom input date component.
 *
 * Input date format could be chosen
 *   'DD-MM-YYYY'
 *   'YYYY-MM-DD'
 *
 * It allows to enter the date as input, as text, from calendar
 */
@Component({
  selector: 'app-formated-date-input',
  templateUrl: './formated-date-input.component.html',
  styleUrls: ['./formated-date-input.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    multi: true,
    useExisting: forwardRef(() => FormatedInputDateComponent),
  }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormatedInputDateComponent extends BaseControlValueAccessor<LocalDate> implements OnInit {
  @Input() input: string = ''; // contains correct date in ISO date format (yyyy-MM-dd) for real DATE input OR empty
  @Input({ transform: transformDateFormat }) dateFormat: DateFormat = DateFormat[YYYY_MM_DD]; // Default value if no input is provided
  @Input() inputID = `input-${Math.random().toString(36).substr(2, 9)}`;
  @Input() title: string = '';
  @Input() bottomText: string = '';
  @Input() additionalClass: InputErrorClass = '';
  @Input() disabled: boolean = false;
  @Input() minDate: string = DEFAULT_DATE_RANGE.minDate;
  @Input() maxDate: string = DEFAULT_DATE_RANGE.maxDate;
  @Input() isRequired: boolean = false; // Indicates whether the input is required
  @Output() inputChange = new EventEmitter<string>();

  formattedDate: string = ''; // Holds the formatted date for display in the text input
  private inputChangeSubject = new Subject<string>();
  private debounceTimeSubject = new Subject<number>();

  constructor(private cdr: ChangeDetectorRef) {
    super();
    this.initializeDebouncedChangeEmitter();
  }

  ngOnInit(): void {
    this.convertInputDateInFormatedDate();
  }

  /**
   * Synchronizes the value from the hidden date input to the formatted text input.
   * Formats the date according to the selected `dateFormat`.
   * @param value - The value from the hidden date input.
   */
  onDateInputChange(value: string): void {
    this.handleValueIfItIsNotProvided(value);
    this.handleValueIfItIsProvided(value);
    this.handleValueIfItIsFromPicker(value);

    this.onChange(this.input);
    this.inputChange.emit(this.input);
  }

  /**
   * Handles input in the text field and formats it according to the selected format.
   */
  onTextInput(event: Event): void {
    const value = this.formatInput((event.target as HTMLInputElement).value);
    this.onDateInputChange(value);
  }

  restrictInput(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab']; // Allowed non-numeric keys
    if (!/^[0-9]$/.test(event.key) && !allowedKeys.includes(event.key)) {
      event.preventDefault(); // Block non-numeric and disallowed keys
      return;
    }

    const inputElement = event.target as HTMLInputElement;

    // Allow input to proceed and process after a short delay
    setTimeout(() => {
      let inputValue = inputElement.value;

      // Format the input to match the desired date format
      inputValue = this.formatInput(inputValue);

      const parts = inputValue.split('-');
      let year: string, month: string, day: string;

      switch (this.dateFormat) {
        case DateFormat[DD_MM_YYYY]:
          [day, month, year] = parts;
          if (day && !this.validateDay(day, month, year)) {
            inputValue = inputValue.slice(0, -2); // Remove last 2 characters
          }
          if (month && !this.validateMonth(month)) {
            inputValue = inputValue.slice(0, -2); // Remove last 2 characters
          }
          if (year && !this.validateYear(year)) {
            inputValue = inputValue.slice(0, -4); // Remove last 4 characters
          }
          break;

        case DateFormat[YYYY_MM_DD]:
          [year, month, day] = parts;
          if (year && !this.validateYear(year)) {
            inputValue = inputValue.slice(0, -4); // Remove last 4 characters
          }
          if (month && !this.validateMonth(month)) {
            inputValue = inputValue.slice(0, -2); // Remove last 2 characters
          }
          if (day && !this.validateDay(day, month, year)) {
            inputValue = inputValue.slice(0, -2); // Remove last 2 characters
          }
          break;
      }

      inputElement.value = inputValue;
      this.formattedDate = inputValue;
    });
  }

  /**
   * Write form disabled state to the DOM element (model => view)
   */
  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.additionalClass = 'disabled';
    } else if (this.additionalClass === 'disabled') {
      this.additionalClass = '';
    }
    this.disabled = isDisabled;
    this.cdr.detectChanges();
  }

  /**
   * Write form value to the DOM element (model => view)
   */
  writeValue(value: any): void {
    if (value) {
      value = this.formatInput(value);
    }
    this.onDateInputChange(value);
    this.cdr.detectChanges();
  }

  private handleValueIfItIsFromPicker(value: string): void {
    if (value && this.isStrictValidDate(value, DateFormat[YYYY_MM_DD])) {
      const [year, month, day] = value.split('-');
      this.formattedDate = this.formatDateParts({ year, month, day }, this.dateFormat);
      this.input = value;
    }
  }

  private handleValueIfItIsProvided(value: string): void {
    if (value && this.isValidDate(value)) {
      this.input = '';
      const { year, month, day } = this.extractDateParts(value, this.dateFormat);
      this.formattedDate = this.formatDateParts({ year, month, day }, this.dateFormat);
      // input can contain only correct date in ISO date format (yyyy-MM-dd), so check
      // it strictly here
      if (this.isStrictValidDate(`${year}-${month}-${day}`, DateFormat[YYYY_MM_DD])) {
        this.input = `${year}-${month}-${day}`;
      }
    }
  }

  private handleValueIfItIsNotProvided(value: string): void {
    if (!value) {
      this.formattedDate = value;
      this.input = '';
    }
  }

  /**
   * Converts `this.input` (in ISO format) into `this.formattedDate` based on the selected date format.
   */
  private convertInputDateInFormatedDate(): void {
    if (!this.input) {
      this.formattedDate = '';
      return;
    }

    const { year, month, day } = this.extractDateParts(this.input, DateFormat[YYYY_MM_DD]);
    this.formattedDate = this.formatDateParts({ year, month, day }, this.dateFormat);
  }

  private formatInput(value: string): string {
    // Remove any non-digit characters except dashes
    value = value.replace(/[^0-9]/g, '');

    // Automatically insert dashes at appropriate positions for the selected format
    switch (this.dateFormat) {
      case DateFormat[DD_MM_YYYY]:
        if (value.length > 2) value = value.slice(0, 2) + '-' + value.slice(2); // Add first dash after day
        if (value.length > 5) value = value.slice(0, 5) + '-' + value.slice(5); // Add second dash after month
        break;
      case DateFormat[YYYY_MM_DD]:
        if (value.length > 4) value = value.slice(0, 4) + '-' + value.slice(4); // Add first dash after year
        if (value.length > 7) value = value.slice(0, 7) + '-' + value.slice(7); // Add second dash after month
        break;
    }

    // Limit the length to date format length
    value = value.slice(0, this.dateFormat.length);
    return value;
  }

  /**
   * Validates the day portion of the date.
   * Ensures the day starts with 0, 1, 2, or 3 and is within the valid range for the month.
   * @param day - The day portion as a string.
   * @param month - The month portion as a string.
   * @param year - The year portion as a string.
   * @returns `true` if the day is valid; otherwise, `false`.
   */
  private validateDay(day: string, month: string, year: string): boolean {
    // Allow intermediate state '0'
    if (day?.length === 1 && day === '0') {
      return true;
    }

    // Ensure day starts with 0, 1, 2, or 3
    if (!/^[0-3]/.test(day)) {
      return false;
    }

    // Convert to number for range checking
    const numericDay = Number(day);
    if (numericDay < 1 || numericDay > 31) return false;

    if (month && year && (year.length === 4)) {
      const numericMonth = Number(month);
      const numericYear = Number(year);
      const daysInMonth = new Date(numericYear, numericMonth, 0).getDate();

      // Check if day is within the valid range for the month
      return numericDay <= daysInMonth;
    }

    return true;
  }

  /**
   * Validates the month portion of the date.
   * Ensures the month starts with 0 or 1 and is between 01 and 12.
   * @param month - The month portion as a string.
   * @returns `true` if the month is valid; otherwise, `false`.
   */
  private validateMonth(month: string): boolean {
    // Allow intermediate state '0' or '1'
    if (month?.length === 1 && /^[0-1]$/.test(month)) {
      return true;
    }

    // Ensure month starts with 0 or 1 and is between 01 and 12
    if (!/^[0-1]/.test(month)) {
      return false;
    }

    const numericMonth = Number(month);
    return numericMonth >= 1 && numericMonth <= 12;
  }

  /**
   * Validates the year portion of the date.
   * Ensures the year is between 1970 and 2200.
   * @param year - The year portion as a string.
   * @returns `true` if the year is valid; otherwise, `false`.
   */
  private validateYear(year: string): boolean {
    if (year?.length < 4) return true; // Allow incomplete year entry

    const numericYear = Number(year);
    return numericYear >= 1970 && numericYear <= 2200;
  }

  /**
   * Validates if a full date is valid.
   * Ensures the date matches the selected format and is within the allowed range.
   * Allows for an unfinished date format.
   * @param date - The full date string.
   * @param dateFormat - Thw date format which must be checked. If it is null, use this.dateFormat.
   * @returns `true` if the date is valid; otherwise, `false`.
   */
  private isValidDate(date: string, dateFormat: DateFormat = null): boolean {
    const { year, month, day } = this.extractDateParts(date, dateFormat ?? this.dateFormat);

    return (
      this.validateYear(year) &&
      this.validateMonth(month) &&
      this.validateDay(day, month, year)
    );
  }

  /**
   * The same as isValidDate(), but without allowing an unfinished date format.
   */
  private isStrictValidDate(date: string, dateFormat: DateFormat = null): boolean {
    const { year, month, day } = this.extractDateParts(date, dateFormat ?? this.dateFormat);
    return (this.isValidDate(date, dateFormat ?? this.dateFormat) &&
      year.length === 4 && month.length === 2 && day.length === 2 &&
      new Date(`${year}-${month}-${day}`) >= new Date(this.minDate) &&
      new Date(`${year}-${month}-${day}`) <= new Date(this.maxDate));
  }

  /**
   * Extracts year, month, and day from a formatted date string based on the specified format.
   */
  private extractDateParts(date: string, format: DateFormat): { year: string; month: string; day: string } {
    const parts = date.split('-');
    switch (format) {
      case DateFormat[DD_MM_YYYY]:
        return { day: parts[0], month: parts[1], year: parts[2] };
      case DateFormat[YYYY_MM_DD]:
      default:
        return { year: parts[0], month: parts[1], day: parts[2] };
    }
  }

  /**
   * Formats the date parts into the specified format.
   */
  private formatDateParts(parts: { year: string; month: string; day: string }, format: DateFormat): string {
    const { year, month, day } = parts;
    switch (format) {
      case DateFormat[DD_MM_YYYY]:
        return `${day}-${month}-${year}`;
      case DateFormat[YYYY_MM_DD]:
        return `${year}-${month}-${day}`;
    }
  }

  /**
   * Initializes the debounced emitter for input changes.
   */
  private initializeDebouncedChangeEmitter(): void {
    this.debounceTimeSubject
      .pipe(
        switchMap((debounceTimeMS) =>
          this.inputChangeSubject.pipe(
            debounceTime(debounceTimeMS),
            distinctUntilChanged()
          )
        )
      )
      .subscribe((value) => this.inputChange.emit(value));
  }
}
